import React, { useState, useEffect, forwardRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

export type MediaQueryProps = {
  /** The content to render when media query matches */
  children?: ReactNode;
  /** Render as span instead of div (for a11y when inside buttons) */
  as?: 'div' | 'span';
  /** Media query string (e.g., "@media (min-width: 600px)") or breakpoint key (e.g., "sm") */
  mediaQuery?: string;
  /** Direction for breakpoint shorthand ("up" or "down") */
  direction?: 'down' | 'up';
  /** CSS display value when visible */
  display?: string;
};

type ContentProps = {
  mediaQuery: string;
  display?: string;
  children?: ReactNode;
};

/**
 * MediaQuery component that conditionally renders content based on screen size.
 * Uses MUI's useMediaQuery hook for media query evaluation.
 */
const MediaQuery = forwardRef<HTMLDivElement | HTMLSpanElement, MediaQueryProps>((props, ref) => {
  const theme = useTheme();

  const { as, mediaQuery = 'sm', direction = 'up', ...otherProps } = props;
  let goodMQ = mediaQuery;
  if (mediaQuery.length < 4) {
    goodMQ = theme.breakpoints[direction](mediaQuery as any);
  }

  const doesMatch = useMediaQuery(goodMQ);
  const [shouldUseMediaQuery, setShouldUseMediaQuery] = useState(false);
  useEffect(() => {
    setShouldUseMediaQuery(true);
  }, []);

  if (shouldUseMediaQuery && !doesMatch) {
    return null;
  }

  // div inside button is not valid HTML
  // provide option to render span for a11y purposes
  if (as === 'span') {
    return <ContentSpan ref={ref} mediaQuery={goodMQ} {...otherProps} />;
  }

  return <ContentDiv ref={ref as Ref<HTMLDivElement>} mediaQuery={goodMQ} {...otherProps} />;
});

MediaQuery.displayName = 'MediaQuery';

// Static property for snapshot testing
(MediaQuery as any).snapshot = false;

const contentStyles = css<ContentProps>`
  display: none;

  ${({ mediaQuery }) => mediaQuery} {
    display: ${({ display = 'inherit' }) => display};
  }
`;

const ContentDiv = styled.div<ContentProps>`
  ${contentStyles}
`;

const ContentSpan = styled.span<ContentProps>`
  ${contentStyles}
`;

export default MediaQuery;
